import { allow, defineAbility, deny } from "nuxt-authorization/utils";
import type { AppUserSession as Session } from "../types/session";
import {
  hasAnyPermissionWithSuffix,
  hasGlobalAdminPermission,
  hasPermission,
  type PermissionSuffix,
} from "./permissions";

export const readAccount = defineAbility(
  (s: Pick<Session, "permissions">, customer: string) => {
    /* if the user has any permissions to access the customer, they can read it*/
    if (s.permissions?.find((p) => p.startsWith(`${customer}:`))) {
      return allow();
    } else {
      return deny({
        statusCode: 403,
        message: `Not authorized to view customer ${customer}'s data.`,
      });
    }
  },
);

export const editAnyAccount = defineAbility(
  (s: Pick<Session, "permissions">) => {
    if (hasAnyPermissionWithSuffix(s.permissions ?? [], "customer:edit")) {
      return allow();
    } else {
      return deny({
        statusCode: 403,
        message: "Not authorized to edit any customers",
      });
    }
  },
);

export const inviteUsers = defineAccountAbility("customer:invite");
export const editAccount = defineAccountAbility("customer:edit");
export const createQuote = defineAccountAbility("quote:edit");
export const readQuote = defineAccountAbility("quote:read");
export const editQuote = defineAccountAbility("quote:edit");
export const deleteQuote = defineAccountAbility("quote:edit");
export const submitOrder = defineAccountAbility("order:edit");
export const readOrder = defineAccountAbility("order:read");
export const editOrder = defineAccountAbility("order:edit");

export const authenticated = defineAbility((s: Pick<Session, "user">) => {
  if (s?.user) {
    return allow();
  } else {
    return deny({ statusCode: 401, message: "Authentication required" });
  }
});

export const globalAdmin = defineAbility((s: Pick<Session, "permissions">) => {
  if (hasGlobalAdminPermission(s.permissions ?? [])) {
    return allow();
  } else {
    return deny({ statusCode: 403, message: "Global administration required" });
  }
});

export const hasAccounts = defineAbility((s: Pick<Session, "customers">) => {
  if (s.customers?.length) {
    return allow();
  } else {
    return deny({
      statusCode: 403,
      message: "Requires access to one or more accounts",
    });
  }
});

export const anyAccount = Symbol("any_account");

function defineAccountAbility(p: PermissionSuffix) {
  return defineAbility(
    (
      s: Pick<Session, "permissions">,
      accounts: string | string[] | typeof anyAccount | null | undefined,
      { allowGlobalAdmin = false }: { allowGlobalAdmin?: boolean } = {},
    ) => {
      const allowsAnyAccount = accounts === anyAccount;
      if (accounts == null || accounts === anyAccount) {
        accounts = [];
      } else if (typeof accounts === "string") {
        accounts = [accounts];
      }

      if (
        accounts.some((account) =>
          hasPermission(s.permissions ?? [], `${account}:${p}`),
        )
      ) {
        return allow();
      } else if (
        allowsAnyAccount &&
        hasAnyPermissionWithSuffix(s.permissions ?? [], p)
      ) {
        return allow();
      } else if (allowGlobalAdmin && globalAdmin.original(s).authorized) {
        return allow();
      } else {
        return deny({
          message: "Not authorized",
          statusCode: 403,
        });
      }
    },
  );
}
